import { type FC, useId } from "react"
import styled from "styled-components"
import Values from "values.js"

import { type CommunityPostPollOption } from "@forento/core/models/community"

import { CheckmarkIcon } from "~/components/Icon"
import { lightTextColor } from "~/utilities/styles"

type Props = {
	option: CommunityPostPollOption
	onChange(isSelected: boolean): void
}
const PollOption: FC<Props> = ({ option, onChange }) => {
	const inputId = useId()
	const votePercentage = Math.round(option.votes.share * 100)

	return (
		<Container $isChecked={option.hasUserSelected} $voteShare={option.votes.share}>
			<Input checked={option.hasUserSelected} onChange={() => onChange(!option.hasUserSelected)} id={inputId} />
			<Content htmlFor={inputId}>
				<TopRow>
					<Checkbox $isChecked={option.hasUserSelected}>
						{option.hasUserSelected && <CheckmarkIcon />}
					</Checkbox>
					<Label>{option.label}</Label>
					<Votes>
						<Percentage $isChecked={option.hasUserSelected}>{votePercentage}%</Percentage>
						<VoteCount>{option.votes.count}</VoteCount>
					</Votes>
				</TopRow>
			</Content>
		</Container>
	)
}

const Container = styled.div<{ $isChecked: boolean; $voteShare: number }>`
	position: relative;
	overflow: hidden;
	border: 1px solid ${props => (props.$isChecked ? props.theme.primaryColor : "#e5e7eb")};
	border-radius: 8px;
	background-color: ${props =>
		props.$isChecked ? new Values(props.theme.primaryColor).tint(96).hexString() : "white"};
	box-shadow: ${props =>
		props.$isChecked
			? `0 0 0 2px ${new Values(props.theme.primaryColor).tint(92).hexString()}`
			: "0 1px 2px rgba(16, 24, 40, 0.04)"};
	transition:
		border-color 0.15s,
		background-color 0.15s,
		box-shadow 0.15s;

	&::before {
		position: absolute;
		inset: 0 auto 0 0;
		width: ${props => props.$voteShare * 100}%;
		background-color: ${props => new Values(props.theme.primaryColor).tint(props.$isChecked ? 90 : 95).hexString()};
		content: "";
		transition: width 0.15s;
	}

	&:hover {
		border-color: ${props => props.theme.primaryColor};
		background-color: ${props => new Values(props.theme.primaryColor).tint(97).hexString()};
	}
`

const Input = styled.input.attrs({ type: "checkbox" })`
	position: absolute;
	width: 1px;
	height: 1px;
	opacity: 0;
	pointer-events: none;
`

const Content = styled.label`
	position: relative;
	display: block;
	padding: 7px 9px;
	cursor: pointer;
`

const TopRow = styled.div`
	display: flex;
	align-items: center;
	gap: 8px;
	min-width: 0;
`

const Checkbox = styled.div<{ $isChecked: boolean }>`
	flex: 0 0 auto;
	display: flex;
	align-items: center;
	justify-content: center;
	width: 22px;
	height: 22px;
	border: 1.4px solid ${props => (props.$isChecked ? props.theme.primaryColor : "#d1d5db")};
	color: white;
	background-color: ${props => (props.$isChecked ? props.theme.primaryColor : "white")};
	border-radius: 50%;

	svg {
		width: 14px;
		height: 14px;
	}
`

const Label = styled.p`
	flex: 1;
	min-width: 0;
	font-weight: 600;
	line-height: 1.25;
	overflow-wrap: anywhere;
`

const Votes = styled.span`
	display: flex;
	flex: 0 0 auto;
	align-items: center;
	gap: 5px;
	font-size: 12px;
	color: ${lightTextColor};
`

const Percentage = styled.span<{ $isChecked: boolean }>`
	border-radius: 999px;
	background-color: ${props => new Values(props.theme.primaryColor).tint(props.$isChecked ? 88 : 94).hexString()};
	color: ${props => props.theme.primaryColor};
	font-weight: 700;
	padding: 1px 6px;
`

const VoteCount = styled.span`
	font-variant-numeric: tabular-nums;
`

export default PollOption
